import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MessageSquare, Trophy } from "lucide-react";
import LucideIconByName from "@/components/LucideIconByName";
import { REACTIONS } from "@/components/reactions";

function timeAgoShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
    return `${Math.floor(diff / 31536000)}y ago`;
  } catch { return "—"; }
}

const ROLE_BADGE = {
  admin: { label: "Administrator", className: "text-[var(--tt-primary)]" },
  mod:   { label: "Moderator",     className: "text-[var(--tt-yellow)]" },
  user:  { label: "Member",        className: "text-[var(--tt-text-muted)]" },
};

/** Compact author mini-card shown to the left of every forum post. */
export default function PostAuthorCard({ card }) {
  if (!card) {
    // Loading placeholder
    return (
      <aside className="w-full md:w-44 shrink-0 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded p-4 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-white/5 animate-pulse" />
        <div className="h-3 mt-3 mx-auto w-20 bg-white/5 animate-pulse rounded" />
      </aside>
    );
  }
  const role = ROLE_BADGE[card.role] || ROLE_BADGE.user;
  const trophies = (card.trophies || {});
  const topTrophies = REACTIONS
    .map((r) => ({ ...r, n: trophies[r.kind] || 0 }))
    .filter((r) => r.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, 3);

  return (
    <aside
      data-testid={`post-author-card-${card.user_id}`}
      className="w-full md:w-44 shrink-0 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded p-4 text-center"
    >
      <Link to={`/u/${card.user_id}`} className="block">
        {card.picture ? (
          <img
            src={card.picture}
            alt=""
            className="w-16 h-16 mx-auto rounded-full object-cover border-2 border-[var(--tt-border)] hover:border-[var(--tt-primary)] transition-colors"
          />
        ) : (
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-xl font-extrabold">
            {(card.name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="font-display font-extrabold text-sm mt-2 text-[var(--tt-primary)] hover:underline truncate">
          {card.name}
        </div>
      </Link>
      <div className={`text-[10px] uppercase tracking-wider font-bold ${role.className}`}>
        {role.label}
      </div>
      {card.user_class && (
        <div
          data-testid={`post-author-rank-${card.user_id}`}
          className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: `${card.user_class.color}1A`,
            borderColor: `${card.user_class.color}55`,
            color: card.user_class.color,
          }}
          title={`${card.user_class.posts} posts · ${card.user_class.member_for_days}d on TorHub`}
        >
          <LucideIconByName name={card.user_class.icon} className="w-3 h-3" color={card.user_class.color} strokeWidth={2.4} />
          {card.user_class.name}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-[var(--tt-border)] text-[11px] text-[var(--tt-text-muted)] space-y-1.5">
        <div className="flex items-center justify-center gap-1.5">
          <Calendar className="w-3 h-3" />
          Joined {timeAgoShort(card.joined_at)}
        </div>
        <div className="flex items-center justify-center gap-1.5">
          <MessageSquare className="w-3 h-3" />
          <span className="font-mono-tt font-bold text-white/85">{card.post_count}</span> posts
        </div>
        {card.trophies_total > 0 && (
          <div className="flex items-center justify-center gap-1.5">
            <Trophy className="w-3 h-3 text-[var(--tt-yellow)]" />
            <span className="font-mono-tt font-bold text-[var(--tt-yellow)]">{card.trophies_total}</span> trophies
          </div>
        )}
      </div>
      {topTrophies.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--tt-border)] flex items-center justify-center gap-2">
          {topTrophies.map((r) => (
            <span
              key={r.kind}
              title={`${r.label} · ${r.n}`}
              className="relative inline-flex items-center justify-center"
            >
              <img src={r.url} alt={r.label} className="w-6 h-6" />
              <span className="absolute -bottom-1 -right-1 text-[9px] font-mono-tt font-extrabold bg-black/90 text-white rounded-full w-4 h-4 flex items-center justify-center border border-[var(--tt-border)]">
                {r.n > 9 ? "9+" : r.n}
              </span>
            </span>
          ))}
        </div>
      )}
    </aside>
  );
}
